import React, { useState, useEffect } from 'react';
import Autocomplete from '../../../../../../controls/autocomplete';
import Checkbox from '../../../../../../controls/checkbox';
import Button from '../../../../../../controls/button';
import useSuggestionApi from './useSuggestionApi';
import './AddressForm.scss';

const stringifyAddress = (address, isStreetMissing) => {
    const separator = '; ';
    return (
        address.city +
        separator +
        (isStreetMissing ? '' : address.street + separator) +
        address.houseNr +
        separator +
        address.postalCode +
        separator
    );
};

const validate = (address, isStreetMissing) => {
    return (
        address.city && (isStreetMissing || address.street) && address.houseNr && address.postalCode
    );
};

const AddressForm = ({ onSubmit, onChange, disabled }) => {
    const [address, setAddress] = useState({
        city: '',
        street: '',
        houseNr: '',
        flatNr: '',
        postalCode: ''
    });
    const [isStreetMissing, setIsStreetMissing] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    let textChangeTimeout = null;

    const {
        citySuggestions,
        onCityFieldChange,
        onCityFieldSelected,
        streetSuggestions,
        onStreetFieldChange,
        onStreetFieldSelected,
        onStreetNumberSelected,
        postalCodeSuggestion,
        suggestionsSelected
    } = useSuggestionApi();

    useEffect(() => {
        setAddress({ ...address, postalCode: postalCodeSuggestion });
    }, [postalCodeSuggestion]);

    const onAddressChange = (e) => {
        setAddress({ ...address, [e.target.id]: e.target.value });
        if (e.target.id === 'city') {
            onCityFieldChange(e);
        } else if (e.target.id === 'street') {
            onStreetFieldChange(e);
        }

        if (textChangeTimeout) {
            clearTimeout(textChangeTimeout);
        }
        textChangeTimeout = setTimeout(() => {
            onChange(stringifyAddress(address, isStreetMissing));
        }, 10);
    };

    const onFormSubmit = () => {
        if (!isValidating) {
            setIsValidating(true);
        }
        if (!validate(address, isStreetMissing)) {
            return;
        }
        onSubmit(stringifyAddress(address, isStreetMissing));
    };

    const forbiddenCharacters = [';'];

    return (
        <div className="message-address-form">
            <Autocomplete
                id="city"
                label="Miejscowość"
                autoComplete="off"
                disabled={disabled}
                floating={true}
                valid={!isValidating || address.city}
                value={address.city}
                onChange={(e) => onAddressChange(e)}
                forbiddenCharacters={forbiddenCharacters}
                suggestions={citySuggestions}
                onSuggestionSelect={(suggestionId) => {
                    const suggestion = citySuggestions.find((s) => s.id === suggestionId);
                    if (!suggestion) {
                        return;
                    }
                    setAddress({
                        ...address,
                        city: suggestion.name,
                        street: ''
                    });
                    onCityFieldSelected(suggestion.id);
                }}
                onBlur={() => {
                    if (!suggestionsSelected.city) {
                        setAddress({
                            ...address,
                            city: '',
                            street: ''
                        });
                    }
                }}
            />
            <Checkbox
                disabled={disabled}
                isChecked={isStreetMissing}
                onChange={() => {
                    setIsStreetMissing(!isStreetMissing);
                    setAddress({ ...address, street: '' });
                }}
                clickableText="W mojej miejscowości nie ma ulicy"
            />
            <Autocomplete
                id="street"
                label="Ulica"
                autoComplete="new-password"
                disabled={disabled || isStreetMissing}
                floating={true}
                valid={!isValidating || isStreetMissing || address.street}
                value={address.street}
                onChange={(e) => onAddressChange(e)}
                forbiddenCharacters={forbiddenCharacters}
                suggestions={streetSuggestions}
                onSuggestionSelect={(suggestionId) => {
                    const suggestion = streetSuggestions.find((s) => s.id === suggestionId);
                    if (!suggestion) {
                        return;
                    }
                    setAddress({
                        ...address,
                        street: suggestion.name
                    });
                    onStreetFieldSelected(suggestion.id);
                }}
                onBlur={() => {
                    if (!suggestionsSelected.street) {
                        setAddress({
                            ...address,
                            street: ''
                        });
                    }
                }}
            />
            <Autocomplete
                id="houseNr"
                label="Nr domu"
                autoComplete="new-password"
                disabled={disabled}
                floating={true}
                valid={!isValidating || address.houseNr}
                value={address.houseNr}
                onChange={(e) => onAddressChange(e)}
                onBlur={(e) => onStreetNumberSelected(e.target.value)}
                forbiddenCharacters={forbiddenCharacters}
            />
            <Autocomplete
                id="flatNr"
                label="Nr lokalu"
                autoComplete="new-password"
                disabled={disabled}
                floating={true}
                value={address.flatNr}
                onChange={(e) => onAddressChange(e)}
                forbiddenCharacters={forbiddenCharacters}
            />
            <Autocomplete
                id="postalCode"
                label="Kod pocztowy"
                autoComplete="new-password"
                disabled={disabled}
                floating={true}
                valid={!isValidating || address.postalCode}
                value={address.postalCode}
                onChange={(e) => onAddressChange(e)}
                forbiddenCharacters={forbiddenCharacters}
                mask="99-999"
                onBlur={() => {
                    if (!address.postalCode.match(new RegExp('^\\d{2}-\\d{3}$'))) {
                        setAddress({
                            ...address,
                            postalCode: ''
                        });
                    }
                }}
            />
            <Button className="primary" onClick={onFormSubmit} text="Wyślij" disabled={disabled} />
        </div>
    );
};

export default AddressForm;
